import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { useEffect, useState } from "react";
import { Product } from "@/types/Products";
import { Loader2, Combine } from "lucide-react";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getPaginationRowModel,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import ViewOptimizationDialog from "@/components/products/ViewOptimizationDialog";
import { calculateProfitUplift } from "@/lib/utils";
interface OptimizedProduct extends Product {
    revenueUplift: number;
    profitUplift: number;
    testingPrice: number;
    originalPrice: number;
    best_ppv_delta: number;
    startTimestamp: string | null;
    is_converged: boolean;
}

function percentageUplift(originalPrice: number, ppvDelta: number) {
    if (!originalPrice || !ppvDelta) return 0;
    const uplift = (ppvDelta / originalPrice) * 100;
    return isNaN(uplift) ? 0 : uplift.toFixed(1);
}

function formatTimeSince(timestamp: string | null): string {
    if (!timestamp) return "-";

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "-"; // Handle invalid date strings

    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days === 1 ? "" : "s"} ago`;
    if (hours > 0) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    if (minutes > 0) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    return "just now";
}

// function calculateProfitUplift(optimizationData: any): number {
//     if (
//         !optimizationData?.plot_data?.x_values ||
//         !optimizationData?.plot_data?.y_values ||
//         !optimizationData?.original_price ||
//         !optimizationData?.best_prices?.length
//     ) {
//         return 0;
//     }

//     const xValues = optimizationData.plot_data.x_values;
//     const yValues = optimizationData.plot_data.y_values;

//     // Find index closest to original price
//     const originalIndex = xValues.reduce(
//         (closest: number, value: number, index: number) => {
//             return Math.abs(value - optimizationData.original_price) <
//                 Math.abs(xValues[closest] - optimizationData.original_price)
//                 ? index
//                 : closest;
//         },
//         0
//     );

//     // Find index closest to best price (last one in the array)
//     const bestPrice =
//         optimizationData.best_prices[optimizationData.best_prices.length - 1];
//     const bestIndex = xValues.reduce(
//         (closest: number, value: number, index: number) => {
//             return Math.abs(value - bestPrice) <
//                 Math.abs(xValues[closest] - bestPrice)
//                 ? index
//                 : closest;
//         },
//         0
//     );

//     const originalPpv = yValues[originalIndex];
//     const bestPpv = yValues[bestIndex];

//     // Calculate the difference
//     const uplift = bestPpv / originalPpv - 1;

//     // Handle NaN values by returning 0
//     return isNaN(uplift) ? 0 : uplift;
// }

export function OptimizedProductsCard() {
    const [products, setProducts] = useState<OptimizedProduct[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { session, isDemoMode } = useAuth();
    const [selectedProduct, setSelectedProduct] =
        useState<OptimizedProduct | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    useEffect(() => {
        async function fetchOptimizedProducts() {
            const { data, error } = await supabase
                .from("products")
                .select("*")
                .eq("user_id", session?.user.id);

            if (data) {
                // Create a map of merge_id to variant names
                const mergeGroups: {
                    [key: string]: {
                        product_name: string;
                        variant_name: string;
                    }[];
                } = {};
                data.forEach((product) => {
                    if (product.merge_id) {
                        if (!mergeGroups[product.merge_id]) {
                            mergeGroups[product.merge_id] = [];
                        }
                        mergeGroups[product.merge_id].push({
                            product_name: product.product_name,
                            variant_name: product.variant_name,
                        });
                    }
                });

                // Add mock data for the additional fields
                const enrichedData: OptimizedProduct[] = data
                    .filter(
                        (product) =>
                            product.status === "Optimized" ||
                            product.status === "Optimizing"
                    )
                    .map((product) => ({
                        ...product,
                        // If product has merge_id, use all merged product/variant names, otherwise use original names
                        product_name: product.merge_id
                            ? [
                                  ...new Set(
                                      mergeGroups[product.merge_id].map(
                                          (p) => p.product_name
                                      )
                                  ),
                              ].join(", ")
                            : product.product_name,
                        variant_name: product.merge_id
                            ? mergeGroups[product.merge_id]
                                  .map((p) => p.variant_name)
                                  .join(", ")
                            : product.variant_name,
                        testingPrice: product.optimization_data
                            ? product.optimization_data.current_test_price
                            : null,
                        originalPrice: product.price,
                        profitUplift:
                            product.optimization_progress?.marketsets
                                ?.reduce((max: number, marketset: any) => {
                                    const uplift =
                                        calculateProfitUplift(marketset.data) *
                                        100 *
                                        (isDemoMode ? 3 : 1);
                                    return Math.max(max, uplift);
                                }, 0)
                                .toFixed(0) || "0",
                        startTimestamp:
                            product.optimization_progress?.marketsets?.reduce(
                                (earliest: number | null, marketset: any) => {
                                    const marketsetEarliest =
                                        marketset.data?.timestamps?.[0] ||
                                        marketset.data?.last_updated;
                                    if (!marketsetEarliest) return earliest;
                                    if (!earliest) return marketsetEarliest;
                                    return Math.min(
                                        earliest,
                                        marketsetEarliest
                                    );
                                },
                                null
                            ) || null,
                        is_converged: product.is_converged,
                    }));
                setProducts(enrichedData);
            }
            setIsLoading(false);
        }

        fetchOptimizedProducts();
    }, [session]);

    // Add column definitions
    const columnHelper = createColumnHelper<OptimizedProduct>();

    const handleOpenDialog = (product: OptimizedProduct) => {
        setSelectedProduct(product);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedProduct(null);
    };

    const columns = [
        columnHelper.accessor("product_name", {
            header: "Product",
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    {row.original.merge_id && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Combine className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Merged product</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    <div
                        className="max-w-[200px] truncate"
                        title={row.getValue("product_name")}
                    >
                        {row.getValue("product_name")}
                    </div>
                </div>
            ),
        }),
        columnHelper.accessor("variant_name", {
            header: "Variant",
            cell: ({ row }) => (
                <div
                    className="max-w-[200px] truncate"
                    title={row.getValue("variant_name")}
                >
                    {row.getValue("variant_name")}
                </div>
            ),
        }),
        columnHelper.accessor("status", {
            header: "Status",
            cell: (info) => {
                const product = info.row.original;
                const status =
                    product.is_converged && product.status === "Optimizing"
                        ? "Ready"
                        : info.getValue();
                return <StatusBadge status={status} />;
            },
        }),
        columnHelper.accessor("startTimestamp", {
            header: "Started",
            cell: (info) => <span>{formatTimeSince(info.getValue())}</span>,
        }),
        columnHelper.accessor("profitUplift", {
            header: "Uplift to date",
            cell: (info) => (
                <span className="text-green-600">+{info.getValue()}%</span>
            ),
        }),
        // columnHelper.accessor("testingPrice", {
        //     header: "Testing Price",
        //     cell: (info) => {
        //         const value = info.getValue();
        //         return value ? `£${value.toFixed(2)}` : "-";
        //     },
        // }),
        // columnHelper.accessor("originalPrice", {
        //     header: "Original Price",
        //     cell: (info) => (
        //         <span className="text-muted-foreground">
        //             £{info.getValue().toFixed(2)}
        //         </span>
        //     ),
        // }),
        // {
        //     header: "Actions",
        //     cell: ({ row }) => (
        //         <Button
        //             variant="ghost"
        //             size="sm"
        //             onClick={() => handleOpenDialog(row.original)}
        //         >
        //             <ChartArea className="h-4 w-4" />
        //         </Button>
        //     ),
        // },
    ];

    const table = useReactTable({
        data: products,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 6,
            },
        },
    });

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>Optimization Summary</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        className="h-24 text-center"
                                    >
                                        <div className="flex items-center justify-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Loading products...
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : products.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        className="h-24 text-center"
                                    >
                                        No optimized products found. Go to{" "}
                                        <a
                                            href="/products"
                                            className="text-primary font-semibold"
                                        >
                                            Products
                                        </a>{" "}
                                        to start optimizing your products.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        onClick={() =>
                                            handleOpenDialog(row.original)
                                        }
                                        className={"cursor-pointer"}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <div className="flex items-center justify-end space-x-2 py-4">
                        <div className="space-x-2">
                            <span className="text-sm text-muted-foreground">
                                Page {table.getState().pagination.pageIndex + 1}{" "}
                                of {table.getPageCount()}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                            >
                                Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {selectedProduct && (
                <ViewOptimizationDialog
                    product={selectedProduct}
                    isOpen={isDialogOpen}
                    onClose={handleCloseDialog}
                />
            )}
        </>
    );
}
